import { decodeEntities } from "./entities.js";
import { hashId, slugify } from "./hash.js";
import { extractYear } from "./title.js";
export class XtreamError extends Error {
    code;
    name = "XtreamError";
    constructor(code, detail) {
        super(`${code}${detail ? `: ${detail}` : ""}`);
        this.code = code;
    }
}
/**
 * The ONLY two statuses that refuse. Everything else — "Trial", "Disabled",
 * blank, absent, or panel-specific wording — is allowed through.
 *
 * This is a media player, not a subscription adjudicator. Its job is to play
 * what the user's own source gives it, so it has no business ruling on account
 * states beyond the two words that unambiguously mean "this line is dead".
 * Refusing a paying customer is far worse than admitting a line that will
 * simply fail to play and say so.
 */
const DEAD_ACCOUNT_STATUSES = new Set(["expired", "banned"]);
export class XtreamClient {
    baseUrl;
    username;
    password;
    fetchImpl;
    timeoutMs;
    /**
     * Host used to build stream URLs (/live, /movie, /series). Many panels
     * serve streams from a DIFFERENT host/port than the API (e.g. API on
     * :8080, streams on :80). server_info from the auth call supplies the
     * real values; until then (and as a fallback) this is the API base URL.
     */
    streamBaseUrl;
    constructor(baseUrl, username, password, options = {}) {
        // Whitespace is trimmed from all three. Credentials typed on a phone arrive
        // with a trailing space from predictive typing, or with one pasted in from
        // the provider's email — and a space in the URL builds
        // "http://host /player_api.php", which is not a URL at all. Each of those
        // failed as "Server unreachable", because the panel this was found on
        // drops the connection on a wrong login instead of answering 401 (owner's
        // second provider, 2026-09-17). Xtream credentials are generated
        // alphanumeric, so a surrounding space is never part of a real one.
        this.baseUrl = baseUrl.trim().replace(/\/+$/, "");
        this.username = username.trim();
        this.password = password.trim();
        this.streamBaseUrl = this.baseUrl;
        this.fetchImpl = options.fetchImpl ?? fetch;
        // Catalog responses can be tens of megabytes on real providers. Keep a
        // bounded request while allowing body download/JSON parsing to finish.
        this.timeoutMs = options.timeoutMs ?? 30_000;
    }
    async validate() {
        try {
            const response = await this.call();
            // Capture the stream host BEFORE any credential checks so failed auth
            // still teaches us where streams live (harmless if auth fails).
            this.applyServerInfo(response.server_info);
            if (response.user_info?.auth === 0)
                return { ok: false, reason: "invalid_credentials" };
            if (response.user_info?.auth !== 1)
                return { ok: false, reason: "malformed_response" };
            // auth:1 is NOT proof the account can stream. Most panels keep returning
            // auth:1 for an expired, banned or disabled line — the catalog still
            // lists, every stream 4xx/5xx. Checking only auth lets a dead
            // subscription past the setup gate and produces an app that looks fine
            // and plays nothing.
            // DENYLIST, not an allowlist. An earlier version rejected anything that
            // was not exactly "active" and locked real users out: panels report
            // "Trial" for perfectly good lines, wording varies between panel
            // software, and values arrive with stray whitespace.
            const status = response.user_info?.status;
            if (typeof status === "string" && DEAD_ACCOUNT_STATUSES.has(status.trim().toLowerCase())) {
                return { ok: false, reason: "inactive_account" };
            }
            // exp_date is deliberately NOT used to refuse. A panel that knows a line
            // is dead reports it in `status`; inferring expiry from a timestamp means
            // second-guessing the provider on the strength of a field whose units,
            // timezone and freshness all vary — and every misread locks a paying user
            // out of their own player. Refusing on the two unambiguous statuses is
            // the whole of the policy.
            return { ok: true };
        }
        catch (error) {
            if (error instanceof XtreamError && error.code === "invalid_credentials") {
                return { ok: false, reason: "invalid_credentials" };
            }
            throw error;
        }
    }
    /** Derive the stream base URL from server_info (url + port + protocol). */
    applyServerInfo(serverInfo) {
        const url = serverInfo?.url;
        if (typeof url !== "string" || !url)
            return;
        const protocol = typeof serverInfo?.server_protocol === "string" && serverInfo.server_protocol
            ? serverInfo.server_protocol
            : this.baseUrl.startsWith("https") ? "https" : "http";
        const port = typeof serverInfo?.port === "string" && serverInfo.port
            ? `:${serverInfo.port}`
            : "";
        this.streamBaseUrl = `${protocol}://${url}${port}`;
    }
    getLiveCategories() {
        return this.callArray("get_live_categories");
    }
    getLiveStreams(categoryId) {
        return this.callArray("get_live_streams", categoryId ? { category_id: categoryId } : {});
    }
    getVodCategories() {
        return this.callArray("get_vod_categories");
    }
    getVodStreams(categoryId) {
        return this.callArray("get_vod_streams", categoryId ? { category_id: categoryId } : {});
    }
    getSeriesCategories() {
        return this.callArray("get_series_categories");
    }
    getSeries(categoryId) {
        return this.callArray("get_series", categoryId ? { category_id: categoryId } : {});
    }
    getSeriesInfo(seriesId) {
        return this.call("get_series_info", { series_id: seriesId });
    }
    /** One film's details (plot, cast, director, genre, length, backdrops), asked for when its page opens. */
    getVodInfo(streamId) {
        return this.call("get_vod_info", { vod_id: streamId });
    }
    async getShortEpg(streamId, limit = 10) {
        const value = await this.call("get_short_epg", { stream_id: streamId, limit: String(limit) });
        if (Array.isArray(value))
            return value;
        if (value && typeof value === "object" && Array.isArray(value.epg_listings)) {
            return value.epg_listings;
        }
        throw new XtreamError("malformed_response", "get_short_epg");
    }
    async getSimpleDataTable(streamId) {
        return this.call("get_simple_data_table", { stream_id: streamId });
    }
    /**
     * ONE channel's full archive listing (Section 16 catch-up).
     *
     * `get_short_epg` looks forwards — a handful of upcoming entries — which is no
     * use for replay. `get_simple_data_table` returns the same `epg_listings`
     * shape but carries a WEEK of PAST listings (measured 2026-09-16), which is
     * exactly the span a catch-up page browses. Asking per channel is what keeps
     * it cheap: a week of one channel, not a week of every channel.
     */
    async getArchiveEpg(streamId) {
        const value = await this.getSimpleDataTable(streamId);
        if (Array.isArray(value))
            return value;
        if (value && typeof value === "object" && Array.isArray(value.epg_listings)) {
            return value.epg_listings;
        }
        throw new XtreamError("malformed_response", "get_simple_data_table");
    }
    buildLiveStreamUrl(streamId, output = "m3u8") {
        return `${this.streamBaseUrl}/live/${encodeURIComponent(this.username)}/${encodeURIComponent(this.password)}/${encodeURIComponent(streamId)}.${output}`;
    }
    buildVodStreamUrl(streamId, extension = "mp4") {
        return `${this.streamBaseUrl}/movie/${encodeURIComponent(this.username)}/${encodeURIComponent(this.password)}/${encodeURIComponent(streamId)}.${safeExtension(extension)}`;
    }
    buildSeriesStreamUrl(episodeId, extension = "mp4") {
        return `${this.streamBaseUrl}/series/${encodeURIComponent(this.username)}/${encodeURIComponent(this.password)}/${encodeURIComponent(episodeId)}.${safeExtension(extension)}`;
    }
    async callArray(action, params = {}) {
        const value = await this.call(action, params);
        if (!Array.isArray(value))
            throw new XtreamError("malformed_response", action);
        return value;
    }
    /**
     * The player_api URL for one action — the one builder every request uses, so
     * the APK's native catalog download (apps/web/src/api/nativeCatalog.ts)
     * requests exactly what this client would. It carries the credentials.
     */
    apiUrl(action, params = {}) {
        const url = new URL(`${this.baseUrl}/player_api.php`);
        url.searchParams.set("username", this.username);
        url.searchParams.set("password", this.password);
        if (action)
            url.searchParams.set("action", action);
        for (const [key, value] of Object.entries(params))
            url.searchParams.set(key, value);
        return url;
    }
    async call(action, params = {}) {
        const url = this.apiUrl(action, params);
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), this.timeoutMs);
        try {
            let response;
            try {
                response = await this.fetchImpl(url, { signal: controller.signal, redirect: "manual" });
            }
            catch (error) {
                if (controller.signal.aborted)
                    throw new XtreamError("timeout");
                // The transport's own code (UND_ERR_SOCKET: the panel hung up) as the
                // detail, so a log can tell a dropped connection from a refused one or
                // a DNS failure. Never the URL: it carries the login.
                const cause = error?.cause;
                throw new XtreamError("unreachable", typeof cause?.code === "string" ? cause.code : undefined);
            }
            // Panels signal a rejected login with a status, not always with the
            // documented `{user_info:{auth:0}}` body. XUI-based panels (Krooz /
            // proxpanel among them) answer a bad username or password with a bare
            // HTTP 512 and an EMPTY body, so there is no JSON to inspect for
            // auth === 0. Treating those as a generic http_error made the API report
            // "Server unreachable — check the URL", sending users to re-check a URL
            // that was always correct. 401/403 are the standard equivalents.
            if (response.status === 512 || response.status === 401 || response.status === 403) {
                throw new XtreamError("invalid_credentials", action ?? "auth");
            }
            if (!response.ok)
                throw new XtreamError("http_error", String(response.status));
            let json;
            try {
                json = await response.json();
            }
            catch {
                throw new XtreamError("malformed_response", action ?? "auth");
            }
            if (isAuthFailure(json))
                throw new XtreamError("invalid_credentials");
            return json;
        }
        finally {
            clearTimeout(timer);
        }
    }
}
export function normalizeXtreamCatalog(live, vod, series, sourceId, client, categoryNames = new Map()) {
    return {
        channels: live.flatMap((stream, index) => {
            if (stream.stream_id === undefined)
                return [];
            const providerId = String(stream.stream_id);
            return [{
                    id: hashId(sourceId, providerId),
                    sourceId,
                    name: decodeEntities(stream.name?.trim() ?? "") || `Channel ${index + 1}`,
                    ...(stream.stream_icon ? { logoUrl: stream.stream_icon } : {}),
                    streamUrl: client.buildLiveStreamUrl(providerId),
                    categoryId: categoryId(stream.category_id, categoryNames),
                    ...(stream.epg_channel_id ? { epgChannelId: stream.epg_channel_id } : {}),
                    ...(toBoolean(stream.tv_archive) ? { catchupAvailable: true } : {}),
                    ...(toNumber(stream.tv_archive_duration) !== undefined
                        ? { catchupDays: toNumber(stream.tv_archive_duration) }
                        : {}),
                    ...(toNumber(stream.num) !== undefined ? { number: toNumber(stream.num) } : {}),
                    hidden: false,
                    health: "unknown",
                }];
        }),
        vod: vod.flatMap((item) => {
            if (item.stream_id === undefined)
                return [];
            const providerId = String(item.stream_id);
            const rawTitle = decodeEntities(item.name?.trim() ?? "") || providerId;
            // Panels advertise a `year` field but routinely omit it — the reference
            // provider returns it for 0 of 43,006 VOD items — while putting the year
            // in the title ("Truly Naked - 2026"). Fall back to the title so the
            // Movies page's year sort has data to work with.
            const year = toNumber(item.year) ?? extractYear(rawTitle);
            const backdrop = firstString(item.backdrop_path);
            return [{
                    id: hashId(sourceId, providerId),
                    sourceId,
                    rawTitle,
                    streamUrl: client.buildVodStreamUrl(providerId, item.container_extension || "mp4"),
                    categoryId: categoryId(item.category_id, categoryNames),
                    ...(item.stream_icon ? { posterUrl: item.stream_icon } : {}),
                    ...(backdrop ? { backdropUrl: backdrop } : {}),
                    ...(year !== undefined ? { year } : {}),
                    ...(toNumber(item.tmdb) !== undefined ? { tmdbId: toNumber(item.tmdb) } : {}),
                    ...(firstString(item.trailer) ? { trailerUrl: `https://www.youtube.com/watch?v=${firstString(item.trailer)}` } : {}),
                    ...(toNumber(item.rating) !== undefined ? { rating: toNumber(item.rating) } : {}),
                }];
        }),
        series: series.flatMap((item) => {
            if (item.series_id === undefined)
                return [];
            const providerId = String(item.series_id);
            const backdrop = firstString(item.backdrop_path);
            return [{
                    id: hashId(sourceId, providerId),
                    sourceId,
                    providerId,
                    rawTitle: decodeEntities(item.name?.trim() ?? "") || providerId,
                    categoryId: categoryId(item.category_id, categoryNames),
                    ...(item.cover ? { posterUrl: item.cover } : {}),
                    ...(backdrop ? { backdropUrl: backdrop } : {}),
                    ...(toNumber(item.tmdb) !== undefined ? { tmdbId: toNumber(item.tmdb) } : {}),
                    ...(firstString(item.youtube_trailer) ? { trailerUrl: `https://www.youtube.com/watch?v=${firstString(item.youtube_trailer)}` } : {}),
                    ...(toNumber(item.rating) !== undefined ? { rating: toNumber(item.rating) } : {}),
                    seasons: [],
                }];
        }),
    };
}
/** Pictures a panel's ffprobe mistakes for the video: a cover attached to the file. */
const STILL_CODECS = new Set(["png", "mjpeg", "jpeg", "bmp", "gif", "webp"]);
/**
 * The provider's measurement of one file (ffprobe's stream objects, sent with
 * each episode), reduced to what the app uses: labels ("1080p · 5.1"), the
 * device's decoder check, and the website server's sound decision without
 * probing the file again. Undefined when the panel sent nothing usable — new
 * episodes arrive before the panel has measured them.
 */
export function parseMediaFormat(video, audio) {
    const v = video && typeof video === "object" && !Array.isArray(video) ? video : null;
    const a = audio && typeof audio === "object" && !Array.isArray(audio) ? audio : null;
    const media = {};
    const videoCodec = typeof v?.codec_name === "string" ? v.codec_name.trim().toLowerCase() : "";
    if (videoCodec && !STILL_CODECS.has(videoCodec)) {
        media.videoCodec = videoCodec;
        const width = toNumber(v.width);
        const height = toNumber(v.height);
        if (width !== undefined && width > 0)
            media.width = width;
        if (height !== undefined && height > 0)
            media.height = height;
        const pixels = typeof v.pix_fmt === "string" ? v.pix_fmt : "";
        if (/p1[02](le|be)?$/.test(pixels))
            media.bitDepth = Number(pixels.match(/p(1[02])/)[1]);
        const transfer = typeof v.color_transfer === "string" ? v.color_transfer : "";
        if (transfer === "smpte2084")
            media.hdr = "hdr10";
        else if (transfer === "arib-std-b67")
            media.hdr = "hlg";
    }
    const audioCodec = typeof a?.codec_name === "string" ? a.codec_name.trim().toLowerCase() : "";
    if (audioCodec) {
        media.audioCodec = audioCodec;
        const channels = toNumber(a.channels);
        if (channels !== undefined && channels > 0)
            media.audioChannels = channels;
    }
    return Object.keys(media).length > 0 ? media : undefined;
}
function withMedia(media) {
    return media ? { media } : {};
}
export function normalizeSeriesInfo(sourceId, seriesId, info, client) {
    const title = info.info?.name?.trim();
    if (!title)
        return null;
    const id = hashId(sourceId, seriesId);
    const episodeGroups = info.episodes && typeof info.episodes === "object" ? info.episodes : {};
    // What the provider says about each season, by number.
    const seasonInfo = new Map((Array.isArray(info.seasons) ? info.seasons : []).map((s) => [toNumber(s.season_number), s]));
    const seasons = Object.entries(episodeGroups).map(([seasonKey, rawEpisodes]) => {
        const seasonNumber = Number(seasonKey);
        const episodes = (Array.isArray(rawEpisodes) ? rawEpisodes : []).flatMap((episode, index) => {
            if (episode.id === undefined)
                return [];
            const episodeId = String(episode.id);
            const extra = episode.info && typeof episode.info === "object" && !Array.isArray(episode.info) ? episode.info : {};
            const still = firstString(episode.movie_image) ?? firstString(extra.movie_image);
            const seconds = toNumber(episode.duration_secs) ?? toNumber(extra.duration_secs);
            const overview = typeof extra.plot === "string" ? extra.plot.trim() : "";
            const airDate = [extra.releasedate, extra.air_date].find((value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))?.slice(0, 10);
            const rating = toNumber(extra.rating);
            return [{
                    id: hashId(sourceId, episodeId),
                    seriesId: id,
                    seasonNumber: Number.isFinite(seasonNumber) ? seasonNumber : Number(episode.season ?? 1),
                    episodeNumber: toNumber(episode.episode_num) ?? index + 1,
                    title: decodeEntities(episode.title?.trim() ?? "") || `Episode ${index + 1}`,
                    streamUrl: client.buildSeriesStreamUrl(episodeId, episode.container_extension || "mp4"),
                    ...(still ? { stillUrl: still } : {}),
                    ...(seconds !== undefined && seconds > 0 ? { runtimeMinutes: Math.round(seconds / 60) } : {}),
                    ...(overview ? { overview } : {}),
                    ...(airDate ? { airDate } : {}),
                    ...(rating !== undefined && rating > 0 ? { rating } : {}),
                    ...withMedia(parseMediaFormat(extra.video, extra.audio)),
                }];
        });
        const about = seasonInfo.get(seasonNumber);
        // A season's air date, else its first episode's.
        const airDate = [about?.air_date, ...episodes.map((e) => e.airDate)].find((value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))?.slice(0, 10);
        const coverUrl = firstString(about?.cover_tmdb) ?? firstString(about?.cover_big) ?? firstString(about?.cover);
        return { seasonNumber, episodes, ...(airDate ? { airDate } : {}), ...(coverUrl ? { coverUrl } : {}) };
    }).filter((season) => Number.isFinite(season.seasonNumber));
    return {
        id,
        sourceId,
        providerId: seriesId,
        rawTitle: title,
        categoryId: "series",
        ...(info.info?.cover ? { posterUrl: info.info.cover } : {}),
        ...(firstString(info.info?.backdrop_path) ? { backdropUrl: firstString(info.info?.backdrop_path) } : {}),
        ...(toNumber(info.info?.tmdb) !== undefined ? { tmdbId: toNumber(info.info?.tmdb) } : {}),
        ...(firstString(info.info?.youtube_trailer) ? { trailerUrl: `https://www.youtube.com/watch?v=${firstString(info.info?.youtube_trailer)}` } : {}),
        ...(toNumber(info.info?.rating) !== undefined ? { rating: toNumber(info.info?.rating) } : {}),
        seasons: seasons.sort((a, b) => a.seasonNumber - b.seasonNumber),
        ...withDetails(info.info ? providerDetails(info.info) : null),
    };
}
function withDetails(details) {
    return details ? { details } : {};
}
/**
 * A film's stream id from its Xtream VOD URL (`…/movie/<user>/<pass>/<id>.<ext>`,
 * redacted or not), for asking the panel about it. Undefined for any other shape
 * (an M3U source's films have no panel to ask).
 */
export function xtreamVodStreamId(streamUrl) {
    return /\/movie\/[^/]+\/[^/]+\/(\d+)\.[a-z0-9]+(?:[?#].*)?$/i.exec(streamUrl)?.[1];
}
/** A film's details from `get_vod_info`, or null when the panel sent none. */
/**
 * Whether a get_vod_info answer carries the film's details at all. A panel turning a request
 * away (seen 2026-10-11: eight asked at once for Home's hero, most came back without them)
 * answers without an `info` object; that is a failed request, never "this film has no details".
 */
export function vodInfoAnswered(answer) {
    const fields = answer?.info;
    return Boolean(fields && typeof fields === "object" && !Array.isArray(fields) && Object.keys(fields).length > 0);
}
export function normalizeVodInfo(info) {
    const fields = info?.info && typeof info.info === "object" && !Array.isArray(info.info) ? info.info : null;
    return fields ? providerDetails(fields) : null;
}
const MAX_CAST = 12;
const MAX_GENRES = 5;
/** What panels put in a field they have nothing for. */
const EMPTY_WORDS = new Set(["n/a", "na", "null", "none", "-", "unknown"]);
/**
 * The details films and series share, cleaned: blanks and placeholders dropped,
 * genre and cast lists split, "02:53:00" read as minutes. Null when nothing is left.
 */
export function providerDetails(info) {
    const overview = cleanText(info.plot) || cleanText(info.description);
    const genres = splitList(info.genre, /\s*[,/|]\s*/, MAX_GENRES);
    const cast = splitList(cleanText(info.cast) || cleanText(info.actors), /\s*,\s*/, MAX_CAST);
    const director = cleanText(info.director);
    const runtimeMinutes = durationMinutes(info.duration) ?? positiveNumber(info.episode_run_time);
    const releaseDate = [info.releasedate, info.releaseDate, info.release_date]
        .find((value) => typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value))
        ?.slice(0, 10);
    const backdropUrl = firstString(info.backdrop_path);
    const trailerUrl = youtubeWatchUrl(info.youtube_trailer);
    const rating = positiveNumber(info.rating);
    const country = cleanText(info.country);
    const details = {
        ...(overview ? { overview } : {}),
        ...(genres.length > 0 ? { genres } : {}),
        ...(cast.length > 0 ? { cast } : {}),
        ...(director ? { director } : {}),
        ...(runtimeMinutes !== undefined ? { runtimeMinutes } : {}),
        ...(releaseDate ? { releaseDate } : {}),
        ...(backdropUrl ? { backdropUrl } : {}),
        ...(trailerUrl ? { trailerUrl } : {}),
        ...(rating !== undefined ? { rating } : {}),
        ...(country ? { country } : {}),
    };
    return Object.keys(details).length > 0 ? details : null;
}
function cleanText(value) {
    const text = typeof value === "string" ? value.trim() : "";
    return EMPTY_WORDS.has(text.toLowerCase()) ? "" : text;
}
function splitList(value, separator, max) {
    const parts = cleanText(value).split(separator).map((part) => cleanText(part)).filter(Boolean);
    return [...new Set(parts)].slice(0, max);
}
/** "02:53:00" → 173. Only the hours:minutes:seconds form panels send. */
function durationMinutes(value) {
    const match = typeof value === "string" ? /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(value.trim()) : null;
    if (!match)
        return undefined;
    const minutes = Number(match[1]) * 60 + Number(match[2]) + (Number(match[3]) >= 30 ? 1 : 0);
    return minutes > 0 ? minutes : undefined;
}
function positiveNumber(value) {
    const number = typeof value === "string" || typeof value === "number" ? toNumber(value) : undefined;
    return number !== undefined && number > 0 ? number : undefined;
}
/** A YouTube id or link as a watch URL, as the list fields' trailers are built. */
function youtubeWatchUrl(value) {
    const text = cleanText(value);
    if (!text)
        return undefined;
    if (/^https?:\/\//i.test(text))
        return text;
    return /^[\w-]{6,}$/.test(text) ? `https://www.youtube.com/watch?v=${text}` : undefined;
}
function isAuthFailure(value) {
    if (!value || typeof value !== "object")
        return false;
    const userInfo = value.user_info;
    return userInfo?.auth === 0 || userInfo?.auth === "0";
}
function safeExtension(extension) {
    const normalized = extension.replace(/[^a-z0-9]/gi, "").toLowerCase();
    return normalized || "mp4";
}
function categoryId(value, names) {
    const key = value === undefined ? "uncategorized" : String(value);
    // Decoded before the slug, so "LIVE | &#127875; Halloween" groups as "live-halloween", not "live-127875-halloween".
    return slugify(decodeEntities(names.get(key) ?? key));
}
function firstString(value) {
    if (Array.isArray(value))
        return value.find((item) => typeof item === "string" && item.length > 0);
    return typeof value === "string" && value.length > 0 ? value : undefined;
}
function toBoolean(value) {
    return value === true || value === 1 || value === "1";
}
function toNumber(value) {
    if (value === undefined || value === "")
        return undefined;
    const number = Number(value);
    return Number.isFinite(number) ? number : undefined;
}
//# sourceMappingURL=xtream.js.map